import AsyncStorage from '@react-native-async-storage/async-storage'
import type { Language, Namespace } from './generated/i18n-meta'

declare const __DEV__: boolean | undefined

const TRANSLATION_PREFIX = '@aikoa/i18n/translations'
const LANGUAGE_VALIDATOR_PREFIX = '@aikoa/i18n/validators'
const DEFAULT_BUILD_KEY = 'aikoa'
const COMMON_NAMESPACE = 'common' as Namespace

export type TranslationData = {
  [key: string]: string | TranslationData
}

export type TranslationCacheStorage = Pick<
  typeof AsyncStorage,
  'getItem' | 'setItem' | 'getAllKeys' | 'multiRemove'
>

type TranslationCacheRuntime = {
  storage: TranslationCacheStorage
  fetch: typeof fetch
}

const defaultRuntime: TranslationCacheRuntime = {
  storage: AsyncStorage,
  fetch: globalThis.fetch.bind(globalThis) as typeof fetch,
}

let translationCacheRuntime = defaultRuntime

const inMemoryTranslations = new Map<string, TranslationData>()
const inFlightTranslations = new Map<string, Promise<TranslationData | null>>()
const inFlightLanguageRefreshes = new Map<string, Promise<boolean>>()

function getBuildKey(buildCacheKey?: string): string {
  return buildCacheKey ?? DEFAULT_BUILD_KEY
}

function getTranslationKey(language: Language, namespace: Namespace): string {
  return `${TRANSLATION_PREFIX}/${language}/${namespace}`
}

function getLanguageValidatorKey(buildCacheKey: string | undefined, language: Language): string {
  return `${LANGUAGE_VALIDATOR_PREFIX}/${getBuildKey(buildCacheKey)}/${language}`
}

function getLanguageRefreshKey(buildCacheKey: string | undefined, language: Language): string {
  return `${getBuildKey(buildCacheKey)}:${language}`
}

function getTranslationRequestKey(language: Language, namespace: Namespace): string {
  return `${language}:${namespace}`
}

function getTranslationUrl(prefix: string, namespace: Namespace, language: Language): string {
  return `${prefix}/${namespace}/${language}.json`
}

function getCommonTranslationUrl(prefix: string, language: Language): string {
  return getTranslationUrl(prefix, COMMON_NAMESPACE, language)
}

export function isTranslationData(value: unknown): value is TranslationData {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false
  }

  for (const childValue of Object.values(value)) {
    if (typeof childValue === 'string') {
      continue
    }

    if (!isTranslationData(childValue)) {
      return false
    }
  }

  return true
}

function parseStoredTranslation(value: unknown): TranslationData | null {
  if (isTranslationData(value)) {
    return value
  }

  if (
    value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    'data' in value &&
    isTranslationData((value as { data: unknown }).data)
  ) {
    return (value as { data: TranslationData }).data
  }

  return null
}

async function loadStoredTranslation(
  language: Language,
  namespace: Namespace
): Promise<TranslationData | null> {
  const key = getTranslationKey(language, namespace)
  const inMemoryCached = inMemoryTranslations.get(key)

  if (inMemoryCached) {
    return inMemoryCached
  }

  try {
    const cached = await translationCacheRuntime.storage.getItem(key)

    if (!cached) {
      return null
    }

    const parsed = JSON.parse(cached) as unknown
    const translation = parseStoredTranslation(parsed)

    if (!translation) {
      return null
    }

    inMemoryTranslations.set(key, translation)

    return translation
  } catch {
    return null
  }
}

async function saveToCache(
  language: Language,
  namespace: Namespace,
  data: TranslationData
): Promise<void> {
  const key = getTranslationKey(language, namespace)
  inMemoryTranslations.set(key, data)

  try {
    await translationCacheRuntime.storage.setItem(key, JSON.stringify(data))
  } catch (error) {
    if (__DEV__) {
      console.error(error)
    }
  }
}

async function getStoredLanguageValidator(
  language: Language,
  buildCacheKey?: string
): Promise<string | null> {
  try {
    return await translationCacheRuntime.storage.getItem(
      getLanguageValidatorKey(buildCacheKey, language)
    )
  } catch {
    return null
  }
}

async function setStoredLanguageValidator(
  validator: string,
  language: Language,
  buildCacheKey?: string
): Promise<void> {
  try {
    await translationCacheRuntime.storage.setItem(
      getLanguageValidatorKey(buildCacheKey, language),
      validator
    )
  } catch (error) {
    if (__DEV__) {
      console.error(error)
    }
  }
}

async function fetchLanguageValidator(prefix: string, language: Language): Promise<string | null> {
  try {
    const response = await translationCacheRuntime.fetch(
      getCommonTranslationUrl(prefix, language),
      {
        method: 'HEAD',
        headers: {
          'cache-control': 'no-cache',
          pragma: 'no-cache',
        },
      }
    )

    if (!response.ok) {
      return null
    }

    return response.headers.get('etag') || response.headers.get('last-modified')
  } catch {
    return null
  }
}

async function waitForLanguageRefresh(language: Language, buildCacheKey?: string): Promise<void> {
  const refreshRequest = inFlightLanguageRefreshes.get(
    getLanguageRefreshKey(buildCacheKey, language)
  )

  if (refreshRequest) {
    await refreshRequest
  }
}

async function fetchTranslation(
  language: Language,
  namespace: Namespace,
  prefix: string
): Promise<TranslationData | null> {
  const url = getTranslationUrl(prefix, namespace, language)

  try {
    const response = await translationCacheRuntime.fetch(url, {
      headers: {
        'cache-control': 'no-cache',
        pragma: 'no-cache',
      },
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch translations: ${response.status}`)
    }

    const data = (await response.json()) as unknown

    if (!isTranslationData(data)) {
      throw new Error('Invalid translation payload')
    }

    await saveToCache(language, namespace, data)

    return data
  } catch (error) {
    if (__DEV__) {
      console.error('Error fetching translations:', error)
    }

    return null
  }
}

async function runNamespaceRequest(
  language: Language,
  namespace: Namespace,
  work: () => Promise<TranslationData | null>
): Promise<TranslationData | null> {
  const requestKey = getTranslationRequestKey(language, namespace)
  const existingRequest = inFlightTranslations.get(requestKey)

  if (existingRequest) {
    return existingRequest
  }

  const request = work().finally(() => {
    inFlightTranslations.delete(requestKey)
  })

  inFlightTranslations.set(requestKey, request)

  return request
}

export function configureTranslationCacheRuntime(
  overrides: Partial<TranslationCacheRuntime>
): void {
  translationCacheRuntime = {
    ...translationCacheRuntime,
    ...overrides,
  }
}

export function resetTranslationCacheRuntime(): void {
  translationCacheRuntime = defaultRuntime
  inMemoryTranslations.clear()
  inFlightTranslations.clear()
  inFlightLanguageRefreshes.clear()
}

export async function clearLanguageTranslations(language: Language): Promise<void> {
  const translationPrefix = `${TRANSLATION_PREFIX}/${language}/`

  for (const inMemoryKey of inMemoryTranslations.keys()) {
    if (inMemoryKey.startsWith(translationPrefix)) {
      inMemoryTranslations.delete(inMemoryKey)
    }
  }

  try {
    const allKeys = await translationCacheRuntime.storage.getAllKeys()
    const translationKeys = allKeys.filter((key) => key.startsWith(translationPrefix))

    if (translationKeys.length > 0) {
      await translationCacheRuntime.storage.multiRemove(translationKeys)
    }
  } catch (error) {
    if (__DEV__) {
      console.error(error)
    }
  }
}

export async function refreshTranslationLanguage(
  language: Language,
  prefix: string,
  buildCacheKey?: string
): Promise<boolean> {
  const refreshKey = getLanguageRefreshKey(buildCacheKey, language)
  const existingRefresh = inFlightLanguageRefreshes.get(refreshKey)

  if (existingRefresh) {
    return existingRefresh
  }

  const refreshRequest = (async () => {
    const nextValidator = await fetchLanguageValidator(prefix, language)

    if (!nextValidator) {
      return false
    }

    const storedValidator = await getStoredLanguageValidator(language, buildCacheKey)

    if (storedValidator === nextValidator) {
      return false
    }

    await clearLanguageTranslations(language)
    await setStoredLanguageValidator(nextValidator, language, buildCacheKey)

    return true
  })().finally(() => {
    inFlightLanguageRefreshes.delete(refreshKey)
  })

  inFlightLanguageRefreshes.set(refreshKey, refreshRequest)
  return refreshRequest
}

export async function getCachedOrFetchNamespace(
  language: Language,
  namespace: Namespace,
  prefix: string,
  buildCacheKey?: string
): Promise<TranslationData | null> {
  await waitForLanguageRefresh(language, buildCacheKey)

  return runNamespaceRequest(language, namespace, async () => {
    const cachedTranslation = await loadStoredTranslation(language, namespace)

    if (cachedTranslation) {
      return cachedTranslation
    }

    return fetchTranslation(language, namespace, prefix)
  })
}
